import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Menu,
    Chip,
    InputAdornment,
    Paper,
    GlobalStyles,
    Autocomplete,
    Tooltip,
    Snackbar,
    Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PrintDisabledIcon from "@mui/icons-material/PrintDisabled";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { z } from "zod";
import {
    Calendar,
    Clock,
    User,
    BookOpen,
    Layers,
    Video,
    MapPin,
    FileText,
    Settings,
    ChevronDown,
    ChevronUp,
    X,
    MoreHorizontal,
    PlusCircle,
    Users,
    Eye
} from "lucide-react";
import { motion } from "framer-motion";
import UploadButtonField from "../../../../Components/Button/UploadButtonField";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useAppSelector, useAppDispatch } from "../../../../Redux/Store/Hooks";
import { createMeetLink, CreateMeetLinkParams, resetCreateMeetLink } from "../../../../Redux/Slices/ScheduledClasses/CreateMeetLinkSlice";
import { createScheduledClass, resetCreateScheduledClass } from "../../../../Redux/Slices/ScheduledClasses/CreateScheduledClassSlice";
import GoogleMeetIcon from "../../../../Assets/Images/meet-icon.png";
import { getScheduleSetupData } from "../../../../Redux/Slices/ScheduledClasses/ScheduleSetupDataSlice";
import { getTutorCourses, TutorCourse } from "../../../../Redux/Slices/ScheduledClasses/TutorCoursesSlice";

interface MeetingModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    onBookletTag?: (booklets: string[]) => void;
    onRefboardUpload?: (file: File) => void;
    selectedDate: Dayjs | null;
    initialData?: any;
}

const scheduleSchema = z.object({
    title: z.string().min(1, "Class title is required"),
    tutor: z.string().min(1, "Please assign a tutor"),
    subject: z.string().min(1, "Please select a subject"),
    topic: z.string().optional(),
    members: z.array(z.union([z.string(), z.number()])).min(1, "Please add at least one member/student"),
    meetingLink: z.string().min(1, "Please add a meeting link"),
});

const MeetingModal: React.FC<MeetingModalProps> = ({
    open,
    onClose,
    onSave,
    onBookletTag,
    onRefboardUpload,
    selectedDate,
    initialData,
}) => {
    const [title, setTitle] = useState("");
    const [startTime, setStartTime] = useState<Dayjs | null>(dayjs().hour(12).minute(30));
    const [endTime, setEndTime] = useState<Dayjs | null>(dayjs().hour(13).minute(30));
    const [localDate, setLocalDate] = useState<Dayjs | null>(null);
    const [meetingLink, setMeetingLink] = useState("");
    const [description, setDescription] = useState("");
    const [booklets, setBooklets] = useState<string[]>([]);
    const [timezone, setTimezone] = useState("UTC");
    const [refboardFiles, setRefboardFiles] = useState<File[]>([]);
    const [worksheetFiles, setWorksheetFiles] = useState<File[]>([]);
    const [existingWorksheets, setExistingWorksheets] = useState<any[]>([]);
    const [existingSampleBooklets, setExistingSampleBooklets] = useState<any[]>([]);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isJoinConfirmOpen, setIsJoinConfirmOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");

    const { data: setupData } = useAppSelector((state) => state.ScheduleSetupDataSlice);
    const apiTutors = setupData?.data?.tutors || [];
    const apiStudents = setupData?.data?.students || [];
    const apiQuestionTypes = setupData?.data?.question_types || [];
    const apiSubjects = setupData?.data?.subjects || [];

    const mappedBooklets = setupData?.data?.mapped_booklets || [];
    const mappedVideoLessons = setupData?.data?.mapped_video_lessons || [];
    const mappedFormulaNotes = setupData?.data?.mapped_formula_notes || [];

    const apiBooklets = setupData?.data?.unmapped_booklets || setupData?.data?.booklets || [];
    const apiVideoLessons = setupData?.data?.unmapped_video_lessons || setupData?.data?.video_lessons || [];
    const apiFormulaNotes = setupData?.data?.unmapped_formula_notes || setupData?.data?.formula_notes || [];

    const courseState = useAppSelector((state: any) => state.TutorCoursesSlice);
    const tutorCoursesResponse = courseState?.data;
    const apiCourses: TutorCourse[] = Array.isArray(tutorCoursesResponse?.data) ? tutorCoursesResponse.data : [];

    const [selectedVideoIds, setSelectedVideoIds] = useState<(string | number)[]>([]);
    const [selectedContentIds, setSelectedContentIds] = useState<(string | number)[]>([]);

    const [removedMappedBooklets, setRemovedMappedBooklets] = useState<(string | number)[]>([]);
    const [removedMappedVideos, setRemovedMappedVideos] = useState<(string | number)[]>([]);
    const [removedMappedNotes, setRemovedMappedNotes] = useState<(string | number)[]>([]);

    // Deduplicate options by unique ID to prevent real data duplicates, 
    // while allowing different students with the same name to exist.
    const deduplicatedOptions = React.useMemo(() => {
        if (!Array.isArray(apiStudents) || apiStudents.length === 0) return [];

        const studentMap = new Map();
        apiStudents.forEach(s => {
            if (s?.id && s?.username) {
                const key = s.username.toLowerCase().trim();
                // Deduplicate by username to prevent UI duplicates 
                if (!studentMap.has(key)) {
                    studentMap.set(key, s);
                }
            }
        });
        return Array.from(studentMap.values());
    }, [apiStudents]);

    const deduplicatedTutors = React.useMemo(() => {
        if (!Array.isArray(apiTutors) || apiTutors.length === 0) return [];
        const tutorMap = new Map();
        apiTutors.forEach(t => {
            if (t?.id && t?.username) {
                const key = t.username.toLowerCase().trim();
                // Deduplicate by username to prevent UI duplicates 
                if (!tutorMap.has(key)) {
                    tutorMap.set(key, t);
                }
            }
        });
        return Array.from(tutorMap.values());
    }, [apiTutors]);

    const deduplicatedQuestionTypes = React.useMemo(() => {
        if (!Array.isArray(apiQuestionTypes) || apiQuestionTypes.length === 0) return [];
        const qtMap = new Map();
        apiQuestionTypes.forEach(q => {
            if (q?.id) {
                const key = (q.name || "").trim();
                if (!qtMap.has(key)) {
                    qtMap.set(key, q);
                }
            }
        });
        return Array.from(qtMap.values());
    }, [apiQuestionTypes]);


    const [tutor, setTutor] = useState("");
    const [subject, setSubject] = useState("");
    const [topic, setTopic] = useState("");
    const [subjectId, setSubjectId] = useState<number | string>("");
    const [selectedCourseId, setSelectedCourseId] = useState<number | string>("");
    const [selectedBookletIds, setSelectedBookletIds] = useState<(string | number)[]>([]);
    const [questionTypeId, setQuestionTypeId] = useState<number | string>("");
    const [members, setMembers] = useState<(string | number)[]>([]); // Using IDs for internal state, allowing string compatibility during transitions
    const [permissions, setPermissions] = useState({
        shareScreen: true,
        recording: true,
        voice: true,
        chat: true,
        fileShare: true
    });
    const [minDate, setMinDate] = useState<Dayjs | null>(null);
    const [maxDate, setMaxDate] = useState<Dayjs | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" as "error" | "success" });
    const dispatch = useAppDispatch();
    const { data: meetLinkData, isLoading: isMeetLoading, error: meetError } = useAppSelector((state) => state.CreateMeetLinkSlice);
    const isReadOnly = !!(initialData?.id && initialData?.startTime && !dayjs(initialData.startTime).isAfter(dayjs()));
    const { data: createData, isLoading: isCreateLoading, error: createError } = useAppSelector((state) => state.CreateScheduledClassSlice);

    useEffect(() => {
        if (open) {
            dispatch(getTutorCourses());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (open && selectedCourseId) {
            const start = localDate || selectedDate || dayjs();
            const end = start.add(1, 'hour');
            dispatch(getScheduleSetupData({
                course_id: String(selectedCourseId),
                start_time: start.isValid() ? start.format("YYYY-MM-DDTHH:mm:ss") : dayjs().format("YYYY-MM-DDTHH:mm:ss"),
                end_time: end.isValid() ? end.format("YYYY-MM-DDTHH:mm:ss") : start.add(1, 'hour').format("YYYY-MM-DDTHH:mm:ss")
            }));
        }
    }, [open, selectedCourseId, dispatch, localDate, selectedDate]);

    const calculateDuration = () => {
        if (startTime && endTime) {
            const diffInMinutes = endTime.diff(startTime, 'minute');
            if (diffInMinutes <= 0) return "";
            const hours = Math.floor(diffInMinutes / 60);
            const minutes = diffInMinutes % 60;
            let str = "";
            if (hours > 0) str += `${hours} hr${hours > 1 ? 's' : ''} `;
            if (minutes > 0) str += `${minutes} min${minutes > 1 ? 's' : ''}`;
            return str.trim();
        }
        return "";
    };

    useEffect(() => {
        if (createData?.status && open) {
            dispatch(resetCreateScheduledClass());
            onClose();
        }
    }, [createData, dispatch, open, onClose]);

    useEffect(() => {
        if (createError && open) {
            console.error("Error creating scheduled class:", createError);
            // You can add a toast notification or alert here
            dispatch(resetCreateScheduledClass());
        }
    }, [createError, dispatch, open]);

    useEffect(() => {
        if (meetLinkData?.status && meetLinkData?.data?.meet_link && open) {
            setMeetingLink(meetLinkData.data.meet_link);
            dispatch(resetCreateMeetLink());
        }
    }, [meetLinkData, dispatch, open]);

    useEffect(() => {
        if (meetError && open) {
            console.error("Error generating meet link:", meetError);
            // Optionally set an alert or error state here
            dispatch(resetCreateMeetLink());
        }
    }, [meetError, dispatch, open]);

    useEffect(() => {
        if (open) {
            // Always clear pending local files and errors when modal opens
            setWorksheetFiles([]);
            setRefboardFiles([]);
            setFormErrors({});
            setRemovedMappedBooklets([]);
            setRemovedMappedVideos([]);
            setRemovedMappedNotes([]);

            if (initialData && initialData.id) {
                setTitle(initialData.title || "");
                setMeetingLink(initialData.meetingLink || "");
                setDescription(initialData.description || "");
                setTutor(initialData.tutor || "");
                setSubject(initialData.subjectName || initialData.subject || "");
                setTopic(initialData.questionTypeName || initialData.topic || "");
                setSubjectId(initialData.subjectId || "");
                setQuestionTypeId(initialData.questionTypeId || "");
                setSelectedCourseId(initialData.courseId || "");

                // Set bounds if they exist in initial data or if we can find the subject
                const initialSubj = apiSubjects.find((s: any) => s.id === initialData.subjectId || s.name === (initialData.subjectName || initialData.subject));
                if (initialSubj) {
                    if (initialSubj.start_date) setMinDate(dayjs(initialSubj.start_date));
                    if (initialSubj.end_date) setMaxDate(dayjs(initialSubj.end_date));

                    // Validate initial date against bounds
                    let startDt = initialData.startTime ? dayjs(initialData.startTime) : (selectedDate || dayjs());
                    if (initialSubj.start_date && startDt.isBefore(dayjs(initialSubj.start_date), 'day')) {
                        startDt = dayjs(initialSubj.start_date);
                    }
                    if (initialSubj.end_date && startDt.isAfter(dayjs(initialSubj.end_date), 'day')) {
                        startDt = dayjs(initialSubj.end_date);
                    }
                    setLocalDate(startDt);
                } else {
                    setLocalDate(selectedDate || dayjs());
                }

                setBooklets(Array.isArray(initialData.booklets) ? initialData.booklets : (initialData.booklet ? [initialData.booklet] : []));
                setMembers(initialData.members || []);
                setExistingWorksheets(initialData.worksheets || []);
                setExistingSampleBooklets(initialData.sampleBooklets || []);
                setLocalDate(dayjs(initialData.date || initialData.startTime));
                setStartTime(dayjs(initialData.startTime));
                setEndTime(dayjs(initialData.endTime));
                setPermissions(initialData.permissions || {
                    shareScreen: true,
                    recording: true,
                    voice: true,
                    chat: true,
                    fileShare: true
                });

                if (Array.isArray(initialData.scheduled_class_resources)) {
                    const videoResource = initialData.scheduled_class_resources.find((r: any) => r.resource_type === "video");
                    const contentResource = initialData.scheduled_class_resources.find((r: any) => r.resource_type === "content");
                    const bookletResource = initialData.scheduled_class_resources.find((r: any) => r.resource_type === "booklet");

                    if (videoResource) setSelectedVideoIds(videoResource.video_ids || []);
                    if (contentResource) setSelectedContentIds(contentResource.content_ids || []);
                    if (bookletResource) setSelectedBookletIds(bookletResource.booklet_ids || []);
                } else {
                    setSelectedVideoIds([]);
                    setSelectedContentIds([]);
                    setSelectedBookletIds(Array.isArray(initialData.bookletIds) ? initialData.bookletIds : []);
                }

            } else if (selectedDate) {
                setTitle("");
                setMeetingLink("");
                setDescription("");
                setTutor("");
                setSubject(initialData?.subject || "");
                setTopic(initialData?.topic || "");
                setSubjectId(initialData?.subjectId || "");
                setQuestionTypeId(initialData?.questionTypeId || "");
                setBooklets([]);
                setMembers([]);
                setExistingWorksheets([]);
                setExistingSampleBooklets([]);
                setPermissions({
                    shareScreen: true,
                    recording: true,
                    voice: true,
                    chat: true,
                    fileShare: true
                });
                setLocalDate(dayjs(selectedDate));
                setStartTime(dayjs(selectedDate).minute(0));
                setEndTime(dayjs(selectedDate).add(1, "hour").minute(0));
            }

            setSelectedVideoIds([]);
            setSelectedContentIds([]);
            setSelectedBookletIds([]);

            // Timezone Detection (Resolved to full IANA name as requested)
            if (initialData && initialData.timezone_str) {
                setTimezone(initialData.timezone_str);
            } else {
                const ianaTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                setTimezone(ianaTz || "UTC");
            }
        }
    }, [open, selectedDate, initialData]);


    const handleGenerateLink = () => {
        const duration = (endTime && startTime) ? endTime.diff(startTime, 'minute') : 60;

        const params: CreateMeetLinkParams = {
            meeting_title: title || "(No title)",
            duration_minutes: duration,
            start_date: localDate?.format("YYYY-MM-DD") || selectedDate?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
            start_time: startTime?.format("HH:mm") || dayjs().format("HH:mm"),
            end_date: localDate?.format("YYYY-MM-DD") || selectedDate?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
            end_time: endTime?.format("HH:mm") || dayjs().add(1, 'hour').format("HH:mm"),
            timezone_str: timezone,
            is_recurring: false
        };

        dispatch(createMeetLink(params));
    };

    const handleSave = () => {
        // Form Validation with Zod
        const validationResult = scheduleSchema.safeParse({
            title,
            tutor,
            subject,
            topic,
            members,
            meetingLink
        });

        if (!validationResult.success) {
            const errors: Record<string, string> = {};
            validationResult.error.errors.forEach((err) => {
                if (err.path[0]) {
                    errors[err.path[0] as string] = err.message;
                }
            });
            setFormErrors(errors);
            return;
        }

        setFormErrors({});
        
        // Construct from_time and to_time in ISO format
        const finalLocalDate = localDate || selectedDate || dayjs();
        const fromDateObj = finalLocalDate.hour(startTime?.hour() || 0).minute(startTime?.minute() || 0).second(0);
        const toDateObj = finalLocalDate.hour(endTime?.hour() || 0).minute(endTime?.minute() || 0).second(0);
        const fromTime = fromDateObj.format("YYYY-MM-DDTHH:mm:ss");
        const toTime = toDateObj.format("YYYY-MM-DDTHH:mm:ss");

        // Collect student IDs - The state now handles IDs directly
        const selectedStudentIds = Array.from(new Set(
            members.map(member => {
                if (typeof member === "number") return member;
                const student = apiStudents.find(s => (s.username || "").trim() === member.trim());
                return student?.id;
            }).filter((id): id is number => id !== undefined)
        ));

        // Find tutor ID
        const selectedTutor = apiTutors.find(t => t.username === tutor);
        const tutorId = selectedTutor ? selectedTutor.id : 0;

        // Find subject and question type IDs if applicable
        const selectedQuestionType = apiQuestionTypes.find(q => q.name === topic);
        const finalQuestionTypeId = questionTypeId || (selectedQuestionType ? selectedQuestionType.id : undefined);
        const finalSubjectId = subjectId || (selectedQuestionType ? selectedQuestionType.subject_id : undefined);

        // Future Time Validation
        if (fromDateObj.isBefore(dayjs())) {
            setFormErrors(prev => ({ ...prev, time: "Please select a future date and time." }));
            return;
        }
        if (toDateObj.isBefore(fromDateObj)) {
            setFormErrors(prev => ({ ...prev, time: "End time must be after start time." }));
            return;
        }

        // Separate files from IDs for sample booklets and worksheets
        const sampleBookletFiles = refboardFiles.filter(file => file instanceof File);
        const worksheetFilesToUpload = worksheetFiles.filter(file => file instanceof File);

        const class_data: any = {
            id: initialData?.id, // include if updating
            class_title: title || "(No title)",
            from_time: fromTime,
            to_time: toTime,
            tutor_id: tutorId,
            participants: selectedStudentIds,
            meeting_link: meetingLink,
            description: description,
            question_type_id: finalQuestionTypeId,
            subject_id: finalSubjectId,
            course_id: selectedCourseId || undefined,
            is_recurring: false,
            timezone_str: timezone,
            scheduled_class_resources: [
                {
                    resource_type: "booklet",
                    booklet_ids: [...existingSampleBooklets.map(b => b.id), ...selectedBookletIds, ...mappedBooklets.filter((m: any) => !removedMappedBooklets.includes(m.id)).map((m: any) => m.id)],
                    video_ids: [],
                    content_ids: []
                },
                {
                    resource_type: "video",
                    booklet_ids: [],
                    video_ids: [...selectedVideoIds, ...mappedVideoLessons.filter((m: any) => !removedMappedVideos.includes(m.id)).map((m: any) => m.id)],
                    content_ids: []
                },
                {
                    resource_type: "content",
                    booklet_ids: [],
                    video_ids: [],
                    content_ids: [...selectedContentIds, ...mappedFormulaNotes.filter((m: any) => !removedMappedNotes.includes(m.id)).map((m: any) => m.id)]
                }
            ],
            sample_booklets: [...existingSampleBooklets.map(b => b.id), ...selectedBookletIds, ...mappedBooklets.filter((m: any) => !removedMappedBooklets.includes(m.id)).map((m: any) => m.id)], // Keeping for backward compatibility if needed by saga
            worksheets: existingWorksheets.map(w => w.id)
        };

        const params = {
            class_data,
            worksheets: worksheetFilesToUpload.length > 0 ? worksheetFilesToUpload : undefined,
            sample_booklet_files: sampleBookletFiles.length > 0 ? sampleBookletFiles : undefined,
        };

        dispatch(createScheduledClass(params as any));

        if (onRefboardUpload && refboardFiles.length > 0) {
            onRefboardUpload(refboardFiles[0]); // Keep existing single callback compatibility if needed
        }

        onSave({
            ...class_data,
            startTime: dayjs(fromTime),
            endTime: dayjs(toTime),
            date: finalLocalDate.format("YYYY-MM-DD"),
            tutor,
            members,
            topic,
            subject,
            booklets,
            permissions,
            worksheetFiles,
            refboardFiles
        });
    };

    // const handlePreviewBooklet = (e: React.MouseEvent, title: string) => {
    //     e.stopPropagation();
    //     const booklet = tutoringData.booklets.find(b => b.title === title);
    //     if (booklet && booklet.drive_link) {
    //         setPreviewUrl(booklet.drive_link);
    //         setPreviewTitle(booklet.title);
    //         setIsPreviewOpen(true);
    //     }
    // };

    const handlePreviewWorksheet = (e: React.MouseEvent, ws: any) => {
        e.stopPropagation();
        if (ws.drive_file_url) {
            setPreviewUrl(ws.drive_file_url);
            setPreviewTitle(ws.drive_file_name);
            setIsPreviewOpen(true);
        }
    };

    const handleDownloadWorksheet = (e: React.MouseEvent, ws: any) => {
        e.stopPropagation();
        const driveId = ws.drive_file_id || ws.driveFileId;
        const driveUrl = ws.drive_file_url || ws.url;
        
        if (driveId) {
            const downloadUrl = `https://docs.google.com/uc?export=download&id=${driveId}`;
            window.open(downloadUrl, "_blank");
        } else if (driveUrl && driveUrl !== "#") {
            window.open(driveUrl, "_blank");
        }
    };

    useEffect(() => {
        const handlePrintPrevention = (e: KeyboardEvent) => {
            if (isPreviewOpen && (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p'))) {
                e.preventDefault();
                alert('Printing is disabled for this document.');
            }
        };

        if (isPreviewOpen) {
            window.addEventListener('keydown', handlePrintPrevention);
        }
        return () => window.removeEventListener('keydown', handlePrintPrevention);
    }, [isPreviewOpen]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "28px",
                        p: 0,
                        overflow: "hidden",
                        boxShadow: "0 25px 80px rgba(0,0,0,0.2)",
                        backdropFilter: "blur(20px)",
                        backgroundColor: "rgba(255, 255, 255, 0.98)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                    }
                }}
            >
                {/* Header / Top Bar with Gradient */}
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 2.5,
                    background: "#F5F8FF",
                    borderBottom: "1px solid rgba(0,0,0,0.04)"
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
                        <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "14px",
                            background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 14px rgba(26, 115, 232, 0.4)"
                        }}>
                            <Calendar size={22} color="#ffffff" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1c1e", lineHeight: 1.2, fontSize: "1.1rem" }}>
                                {isReadOnly ? "Class Details" : (initialData?.id ? "Edit Class" : "Schedule New Class")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#5f6368", fontWeight: 600 }}>
                                {initialData?.id ? "Modify your scheduled class" : "Plan a new learning session"}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: "#5f6368",
                            bgcolor: "#E8EAF6",
                            borderRadius: "12px",
                            padding: "8px",
                            "&:hover": { bgcolor: "#D1D9FF", color: "#1a1c1e" }
                        }}
                    >
                        <X size={20} />
                    </IconButton>
                </Box>

                <DialogContent
                    sx={{
                        px: 4,
                        py: 3,
                        "&::-webkit-scrollbar": { display: "none" },
                        overflowX: "hidden"
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <TextField
                                fullWidth
                                placeholder="Enter Class Title *"
                                variant="standard"
                                value={title}
                                disabled={isReadOnly}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    if (formErrors.title) setFormErrors(prev => ({ ...prev, title: "" }));
                                }}
                                autoFocus={!isReadOnly}
                                error={!!formErrors.title}
                                helperText={formErrors.title}
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: "22px",
                                        fontWeight: 800,
                                        color: "#BDC1C6",
                                        letterSpacing: "-0.01em",
                                        "& input": {
                                            padding: 0,
                                        },
                                        "& input::placeholder": { color: "#BDC1C6", opacity: 0.8 }
                                    }
                                }}
                            />
                            <Box sx={{
                                height: "4px",
                                width: "70px",
                                bgcolor: formErrors.title ? "#d32f2f" : "#4285F4",
                                mt: 0.5,
                                borderRadius: "4px",
                            }} />
                        </Box>


                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                            {/* Time & Date Selection */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "16px",
                                    background: "#FFF4EB",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(255, 167, 38, 0.1)"
                                }}>
                                    <Clock size={20} color="#F57C00" />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#3c4043", mb: 0.5 }}>
                                        Date & Time <span style={{ color: '#d32f2f' }}>*</span>
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    value={localDate}
                                                    disabled={isReadOnly}
                                                    minDate={minDate || undefined}
                                                    maxDate={maxDate || undefined}
                                                    onChange={(v) => {
                                                        setLocalDate(v);
                                                        if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                    }}
                                                    format="DD MMM YYYY"
                                                    slotProps={{
                                                        textField: {
                                                            variant: "outlined",
                                                            size: "small",
                                                            sx: {
                                                                width: 190,
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: "12px",
                                                                    bgcolor: "#f8faff",
                                                                    border: "1px solid rgba(0,0,0,0.06)",
                                                                    "& fieldset": { border: "none" },
                                                                    "&:hover": { bgcolor: "#ffffff", borderColor: "#1a73e8" },
                                                                    "&.Mui-focused": { bgcolor: "#ffffff", borderColor: "#1a73e8", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.08)" }
                                                                }
                                                            }
                                                        },
                                                        popper: {
                                                            sx: {
                                                                "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                "& .MuiPickersDay-root": {
                                                                    borderRadius: "50% !important",
                                                                    "&.Mui-selected": {
                                                                        bgcolor: "#1a73e8 !important",
                                                                        color: "#fff !important",
                                                                        background: "#1a73e8 !important",
                                                                        "&:hover": { bgcolor: "#1557b0 !important" }
                                                                    },
                                                                    "&.MuiPickersDay-today": {
                                                                        borderColor: "#1a73e8 !important",
                                                                        borderWidth: "1px !important",
                                                                        color: "#1a73e8 !important"
                                                                    }
                                                                },
                                                                "& .MuiPickersDay-root:not(.Mui-selected):hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" },
                                                                "& .MuiPickersDay-root.Mui-selected.MuiPickersDay-dayWithMargin": { backgroundColor: "#1a73e8 !important" }
                                                            }
                                                        }
                                                    }}
                                                />
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <TimePicker
                                                        value={startTime}
                                                        disabled={isReadOnly}
                                                        onChange={(v) => {
                                                            setStartTime(v);
                                                            if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                variant: "outlined",
                                                                size: "small",
                                                                sx: {
                                                                    width: 120,
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: "12px",
                                                                        bgcolor: "#f8faff",
                                                                        border: "1px solid rgba(0,0,0,0.06)",
                                                                        "& fieldset": { border: "none" },
                                                                        "&:hover": { bgcolor: "#ffffff", borderColor: "#1a73e8" },
                                                                        "&.Mui-focused": { bgcolor: "#ffffff", borderColor: "#1a73e8", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.08)" }
                                                                    }
                                                                }
                                                            },
                                                            popper: {
                                                                sx: {
                                                                    "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                    "& .MuiMultiSectionDigitalClock-root": { bgcolor: "#fff !important" },
                                                                    "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": {
                                                                        bgcolor: "#1a73e8 !important",
                                                                        color: "#fff !important",
                                                                        background: "#1a73e8 !important"
                                                                    },
                                                                    "& .MuiMultiSectionDigitalClockSection-item:hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="textSecondary">–</Typography>
                                                    <TimePicker
                                                        value={endTime}
                                                        disabled={isReadOnly}
                                                        onChange={(v) => {
                                                            setEndTime(v);
                                                            if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                variant: "outlined",
                                                                size: "small",
                                                                sx: {
                                                                    width: 120,
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: "12px",
                                                                        bgcolor: "#f8faff",
                                                                        border: "1px solid rgba(0,0,0,0.06)",
                                                                        "& fieldset": { border: "none" },
                                                                        "&:hover": { bgcolor: "#ffffff", borderColor: "#1a73e8" },
                                                                        "&.Mui-focused": { bgcolor: "#ffffff", borderColor: "#1a73e8", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.08)" }
                                                                    }
                                                                }
                                                            },
                                                            popper: {
                                                                sx: {
                                                                    "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                    "& .MuiMultiSectionDigitalClock-root": { bgcolor: "#fff !important" },
                                                                    "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": {
                                                                        bgcolor: "#1a73e8 !important",
                                                                        color: "#fff !important",
                                                                        background: "#1a73e8 !important"
                                                                    },
                                                                    "& .MuiMultiSectionDigitalClockSection-item:hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </LocalizationProvider>
                                        </Box>
                                        {calculateDuration() && (
                                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <Typography variant="caption" sx={{
                                                    color: "#1a73e8",
                                                    fontWeight: 800,
                                                    bgcolor: "#EBF1FF",
                                                    px: 2.5,
                                                    py: 1,
                                                    fontSize: "12px",
                                                    borderRadius: "40px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.2,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.8px",
                                                    boxShadow: "0 4px 12px rgba(26, 115, 232, 0.15)"
                                                }}>
                                                    <Clock size={16} strokeWidth={3} /> DURATION: {calculateDuration()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {formErrors.time && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>{formErrors.time}</Typography>}
                                </Box>
                            </Box>
                            {/* Class Details Section */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "16px",
                                    background: "#F5EBFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(156, 39, 176, 0.1)"
                                }}>
                                    <Layers size={24} color="#9C27B0" />
                                </Box>
                                <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#5f6368", mb: 0.8, ml: 0.5, fontSize: "13px" }}>Course Selection</Typography>
                                        <Autocomplete
                                            id="course-autocomplete"
                                            options={apiCourses}
                                            disabled={isReadOnly}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.full_name || "")}
                                            value={apiCourses.find((c: any) => c.course_id === selectedCourseId) || null}
                                            onChange={(_, newValue: any) => {
                                                setSelectedCourseId(newValue ? newValue.course_id : "");
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Course"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>

                                    <FormControl fullWidth size="small" error={!!formErrors.subject}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: formErrors.subject ? "#d32f2f" : "#5f6368", mb: 0.8, ml: 0.5, fontSize: "13px" }}>Subject <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                        <Autocomplete
                                            id="subject-autocomplete"
                                            options={apiSubjects}
                                            disabled={isReadOnly}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || "")}
                                            value={apiSubjects.find((s: any) => s.id === subjectId || s.name === subject) || null}
                                            onChange={(_, newValue: any) => {
                                                const selectedName = newValue ? newValue.name : "";
                                                setSubject(selectedName);
                                                if (newValue) {
                                                    setSubjectId(newValue.id);
                                                    setTopic("");
                                                    setQuestionTypeId("");
                                                    if (newValue.start_date) setMinDate(dayjs(newValue.start_date));
                                                    if (newValue.end_date) setMaxDate(dayjs(newValue.end_date));
                                                }
                                                if (formErrors.subject) setFormErrors(prev => ({ ...prev, subject: "" }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Subject"
                                                    error={!!formErrors.subject}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>

                                    <FormControl fullWidth size="small" sx={{ gridColumn: "span 2" }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#5f6368", mb: 0.8, ml: 0.5, fontSize: "13px" }}>Sub-type / Topic</Typography>
                                        <Autocomplete
                                            id="topic-autocomplete"
                                            options={deduplicatedQuestionTypes}
                                            disabled={isReadOnly}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || "")}
                                            value={deduplicatedQuestionTypes.find(q => q.name === topic) || null}
                                            onChange={(_, newValue) => {
                                                setTopic(newValue ? newValue.name : "");
                                                if (newValue) {
                                                    setQuestionTypeId(newValue.id);
                                                    setSubjectId(newValue.subject_id);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Topic"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Box>
                            </Box>

                            {/* Members Section */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "16px",
                                    background: "#E3FAFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(0, 188, 212, 0.1)"
                                }}>
                                    <Users size={24} color="#00BCD4" />
                                </Box>
                                <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                                    <FormControl fullWidth size="small" error={!!formErrors.tutor}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: formErrors.tutor ? "#d32f2f" : "#5f6368", mb: 0.8, ml: 0.5, fontSize: "13px" }}>Assign to Tutor <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                        <Autocomplete
                                            id="tutor-autocomplete"
                                            options={deduplicatedTutors}
                                            disabled={isReadOnly}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.username || "")}
                                            value={deduplicatedTutors.find(t => t.username === tutor) || null}
                                            onChange={(_, newValue) => {
                                                setTutor(newValue ? newValue.username : "");
                                                if (formErrors.tutor) setFormErrors(prev => ({ ...prev, tutor: "" }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Tutor"
                                                    error={!!formErrors.tutor}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            minHeight: "45px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>

                                    <FormControl fullWidth size="small" error={!!formErrors.members}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: formErrors.members ? "#d32f2f" : "#5f6368", mb: 0.8, ml: 0.5, fontSize: "13px" }}>Add Students <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                        <Autocomplete
                                            multiple
                                            id="members-autocomplete"
                                            disabled={isReadOnly}
                                            options={deduplicatedOptions}
                                            getOptionLabel={(option) => typeof option === "string" ? option : (option.username || "")}
                                            value={members.map(id => deduplicatedOptions.find(s => s.id === id || s.username === id) || id).filter(Boolean)}
                                            onChange={(_, newValue) => {
                                                setMembers(newValue.map(v => typeof v === "string" ? v : v.id));
                                                if (formErrors.members) setFormErrors(prev => ({ ...prev, members: "" }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Students"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            minHeight: "45px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                        }
                                                    }}
                                                />
                                            )}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={index}
                                                        label={typeof option === "string" ? option : option.username}
                                                        size="small"
                                                        sx={{ bgcolor: "#e8f0fe", color: "#1a73e8", fontWeight: 600, borderRadius: "10px" }}
                                                    />
                                                ))
                                            }
                                        />
                                    </FormControl>
                                </Box>
                            </Box>

                            {/* Reference Section */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "16px",
                                    background: "#EBF8F0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(76, 175, 80, 0.1)"
                                }}>
                                    <FileText size={24} color="#4CAF50" />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#5f6368", mb: 0.8, ml: 0.5, display: "block", fontSize: "13px" }}>Reference Section</Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 0.5, ml: 0.5, display: "block" }}>Booklet </Typography>

                                        <Autocomplete
                                            multiple
                                            id="booklet-autocomplete"
                                            options={[...mappedBooklets, ...apiBooklets]}
                                            filterOptions={(options, params) => {
                                                const match = params.inputValue.toLowerCase();
                                                return options.filter(opt =>
                                                    !mappedBooklets.some((m: any) => m.id === opt.id) && 
                                                    (opt.name || opt.title || "").toLowerCase().includes(match)
                                                );
                                            }}
                                            disabled={isReadOnly}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || option.title || "")}
                                            value={[
                                                ...mappedBooklets.filter((b: any) => !removedMappedBooklets.includes(b.id)),
                                                ...apiBooklets.filter((b: any) => selectedBookletIds.includes(b.id))
                                            ]}
                                            onChange={(_, newValue: any[]) => {
                                                const unmappedIds = newValue.filter(opt => !mappedBooklets.some((m: any) => m.id === opt.id)).map(opt => opt.id);
                                                setSelectedBookletIds(unmappedIds);
                                                
                                                const missingMappedIds = mappedBooklets.filter((m: any) => !newValue.some(opt => opt.id === m.id)).map((m: any) => m.id);
                                                setRemovedMappedBooklets(missingMappedIds);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Booklets"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "14px",
                                                            bgcolor: "#f8faff",
                                                            transition: "all 0.2s ease",
                                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" },
                                                            "&:hover": { bgcolor: "#ffffff" },
                                                            "&.Mui-focused": { bgcolor: "#ffffff", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.08)" }
                                                        }
                                                    }}
                                                />
                                            )}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => {
                                                    const isMapped = mappedBooklets.some((m: any) => m.id === option.id);
                                                    return (
                                                        <Chip
                                                            {...getTagProps({ index })}
                                                            key={index}
                                                            label={typeof option === 'string' ? option : (option.name || option.title || "")}
                                                            size="small"
                                                            sx={isMapped ? {
                                                                bgcolor: "#f3e5f5",
                                                                color: "#9c27b0",
                                                                fontWeight: 500,
                                                                borderRadius: "8px"
                                                            } : {
                                                                bgcolor: "#e8f0fe",
                                                                color: "#1a73e8",
                                                                fontWeight: 500,
                                                                borderRadius: "8px"
                                                            }}
                                                        />
                                                    );
                                                })
                                            }
                                        />
                                    </Box>

                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 0.5, ml: 0.5, display: "block" }}>Video Lesson</Typography>
                                            <Autocomplete
                                                multiple
                                                id="video-lesson-autocomplete"
                                                options={[...mappedVideoLessons, ...apiVideoLessons]}
                                                filterOptions={(options, params) => {
                                                    const match = params.inputValue.toLowerCase();
                                                    return options.filter(opt =>
                                                        !mappedVideoLessons.some((m: any) => m.id === opt.id) && 
                                                        (opt.title || "").toLowerCase().includes(match)
                                                    );
                                                }}
                                                disabled={isReadOnly}
                                                getOptionLabel={(option) => typeof option === 'string' ? option : (option.title || "")}
                                                value={[
                                                    ...mappedVideoLessons.filter((v: any) => !removedMappedVideos.includes(v.id)),
                                                    ...apiVideoLessons.filter((v: any) => selectedVideoIds.includes(v.id))
                                                ]}
                                                onChange={(_, newValue: any[]) => {
                                                    const unmappedIds = newValue.filter(opt => !mappedVideoLessons.some((m: any) => m.id === opt.id)).map(opt => opt.id);
                                                    setSelectedVideoIds(unmappedIds);
                                                    
                                                    const missingMappedIds = mappedVideoLessons.filter((m: any) => !newValue.some(opt => opt.id === m.id)).map((m: any) => m.id);
                                                    setRemovedMappedVideos(missingMappedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select Videos"
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "14px",
                                                                bgcolor: "#f8faff",
                                                                "& fieldset": { borderColor: "rgba(0,0,0,0.06)" }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => {
                                                        const isMapped = mappedVideoLessons.some((m: any) => m.id === option.id);
                                                        return (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={index}
                                                                label={typeof option === 'string' ? option : (option.title || "")}
                                                                size="small"
                                                                sx={isMapped ? { bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 500, borderRadius: "8px" } : { bgcolor: "#e8f0fe", color: "#1a73e8", fontWeight: 500, borderRadius: "8px" }}
                                                            />
                                                        );
                                                    })
                                                }
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 0.5, ml: 0.5, display: "block" }}>Study Notes</Typography>
                                            <Autocomplete
                                                multiple
                                                id="study-notes-autocomplete"
                                                options={[...mappedFormulaNotes, ...apiFormulaNotes]}
                                                filterOptions={(options, params) => {
                                                    const match = params.inputValue.toLowerCase();
                                                    return options.filter(opt =>
                                                        !mappedFormulaNotes.some((m: any) => m.id === opt.id) && 
                                                        (opt.name || opt.title || "").toLowerCase().includes(match)
                                                    );
                                                }}
                                                disabled={isReadOnly}
                                                getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || option.title || "")}
                                                value={[
                                                    ...mappedFormulaNotes.filter((n: any) => !removedMappedNotes.includes(n.id)),
                                                    ...apiFormulaNotes.filter((n: any) => selectedContentIds.includes(n.id))
                                                ]}
                                                onChange={(_, newValue: any[]) => {
                                                    const unmappedIds = newValue.filter(opt => !mappedFormulaNotes.some((m: any) => m.id === opt.id)).map(opt => opt.id);
                                                    setSelectedContentIds(unmappedIds);
                                                    
                                                    const missingMappedIds = mappedFormulaNotes.filter((m: any) => !newValue.some(opt => opt.id === m.id)).map((m: any) => m.id);
                                                    setRemovedMappedNotes(missingMappedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select Notes"
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "14px",
                                                                bgcolor: "#f8faff",
                                                                "& fieldset": { borderColor: "rgba(0,0,0,0.06)" }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => {
                                                        const isMapped = mappedFormulaNotes.some((m: any) => m.id === option.id);
                                                        return (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={index}
                                                                label={typeof option === 'string' ? option : (option.name || option.title || "")}
                                                                size="small"
                                                                sx={isMapped ? { bgcolor: "#fff3e0", color: "#ef6c00", fontWeight: 500, borderRadius: "8px" } : { bgcolor: "#e8f0fe", color: "#1a73e8", fontWeight: 500, borderRadius: "8px" }}
                                                            />
                                                        );
                                                    })
                                                }
                                            />
                                        </Box>
                                    </Box>


                                    {(existingSampleBooklets.length > 0 || refboardFiles.length > 0) && (
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 0.5, ml: 0.5, display: "block" }}>Reference Notes</Typography>
                                            {existingSampleBooklets.map((sb, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2, borderRadius: "12px" }}>
                                                    <BookOpen size={20} color="#1a73e8" />
                                                    <Typography variant="body2" sx={{ flex: 1 }}>{sb.drive_file_name}</Typography>
                                                    <IconButton size="small" onClick={(e) => handlePreviewWorksheet(e, sb)} color="primary"><Eye size={18} /></IconButton>
                                                    <IconButton size="small" onClick={() => setExistingSampleBooklets(prev => prev.filter((_, i) => i !== idx))} color="error"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                                                </Paper>
                                            ))}
                                            {refboardFiles.map((file, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2, borderRadius: "12px", bgcolor: "#e8f0fe", borderStyle: "dashed", borderColor: "#1a73e8" }}>
                                                    <CloudUploadIcon sx={{ fontSize: 20, color: "#1a73e8" }} />
                                                    <Typography variant="body2" sx={{ flex: 1, color: "#1a73e8" }}>{file.name}</Typography>
                                                    <IconButton size="small" onClick={() => setRefboardFiles(prev => prev.filter((_, i) => i !== idx))} color="error"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                                                </Paper>
                                            ))}
                                        </Box>
                                    )}

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: "16px",
                                            borderStyle: "dashed",
                                            borderWidth: "2px",
                                            borderColor: "#dadce0",
                                            bgcolor: "#fcfcff",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            "&:hover": { borderColor: "#1a73e8", bgcolor: "#f3f6ff" },
                                            transition: "all 0.3s ease"
                                        }}
                                    >
                                        <input
                                            type="file"
                                            id="refboard-upload-modal"
                                            multiple
                                            accept=".pdf,.docx,.xls,.xlsx,.txt,.md"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                setRefboardFiles(prev => [...prev, ...files]);
                                                e.target.value = "";
                                            }}
                                        />
                                        <label htmlFor="refboard-upload-modal" style={{ width: "100%", cursor: "pointer" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                                <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: "linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <CloudUploadIcon sx={{ fontSize: 20, color: "#ffffff" }} />
                                                </Box>
                                                <Box sx={{ textAlign: "left" }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1c1e" }}>Upload Reference Notes</Typography>
                                                    <Typography variant="caption" sx={{ color: "#70757a" }}>PDF, DOCX (Max 10MB)</Typography>
                                                </Box>
                                            </Box>
                                        </label>
                                    </Paper>
                                </Box>
                            </Box>

                            {/* Worksheet Section */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "16px",
                                    background: "#E3FAFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(0, 188, 212, 0.1)"
                                }}>
                                    <Layers size={24} color="#00BCD4" />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#5f6368", mb: 0.8, ml: 0.5, display: "block", fontSize: "13px" }}>Worksheet Section</Typography>

                                    {(existingWorksheets.length > 0 || worksheetFiles.length > 0) && (
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
                                            {existingWorksheets.map((ws, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2, borderRadius: "12px" }}>
                                                    <FileText size={20} color="#00BCD4" />
                                                    <Typography variant="body2" sx={{ flex: 1 }}>{ws.drive_file_name}</Typography>
                                                    <IconButton size="small" onClick={(e) => handlePreviewWorksheet(e, ws)} color="primary" title="Preview"><Eye size={18} /></IconButton>
                                                    <IconButton size="small" onClick={(e) => handleDownloadWorksheet(e, ws)} color="secondary" title="Download"><DownloadIcon sx={{ fontSize: 18 }} /></IconButton>
                                                    <IconButton size="small" onClick={() => setExistingWorksheets(prev => prev.filter((_, i) => i !== idx))} color="error" title="Remove"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                                                </Paper>
                                            ))}
                                            {worksheetFiles.map((file, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2, borderRadius: "12px", bgcolor: "#e3f2fd", borderStyle: "dashed", borderColor: "#00BCD4" }}>
                                                    <CloudUploadIcon sx={{ fontSize: 20, color: "#00BCD4" }} />
                                                    <Typography variant="body2" sx={{ flex: 1, color: "#00BCD4" }}>{file.name}</Typography>
                                                    <IconButton size="small" onClick={() => setWorksheetFiles(prev => prev.filter((_, i) => i !== idx))} color="error"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                                                </Paper>
                                            ))}
                                        </Box>
                                    )}

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: "16px",
                                            borderStyle: "dashed",
                                            borderWidth: "2px",
                                            borderColor: "#dadce0",
                                            bgcolor: "#fcfcff",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            "&:hover": { borderColor: "#00BCD4", bgcolor: "#f3f6ff" },
                                            transition: "all 0.3s ease"
                                        }}
                                    >
                                        <input
                                            type="file"
                                            id="worksheet-upload-modal"
                                            multiple
                                            accept=".pdf,.docx,.xls,.xlsx,.txt,.md"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                setWorksheetFiles(prev => [...prev, ...files]);
                                                e.target.value = "";
                                            }}
                                        />
                                        <label htmlFor="worksheet-upload-modal" style={{ width: "100%", cursor: "pointer" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                                <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: "linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <CloudUploadIcon sx={{ fontSize: 20, color: "#ffffff" }} />
                                                </Box>
                                                <Box sx={{ textAlign: "left" }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1c1e" }}>Upload Worksheets</Typography>
                                                    <Typography variant="caption" sx={{ color: "#70757a" }}>PDF, DOCX (Max 10MB)</Typography>
                                                </Box>
                                            </Box>
                                        </label>
                                    </Paper>
                                </Box>
                            </Box>

                            {/* Description Section */}
                            <Box sx={{ display: "flex", gap: 2.5 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "12px",
                                    background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: "0 4px 10px rgba(76, 175, 80, 0.1)"
                                }}>
                                    <FileText size={22} color="#4caf50" />
                                </Box>
                                <TextField
                                    fullWidth
                                    placeholder="Add class description or notes..."
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    value={description}
                                    disabled={isReadOnly}
                                    onChange={(e) => setDescription(e.target.value)}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "20px",
                                            bgcolor: "#f8faff",
                                            transition: "all 0.2s ease",
                                            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
                                            "&:hover fieldset": { borderColor: "#1a73e8" },
                                            "&.Mui-focused fieldset": { borderColor: "#1a73e8" },
                                            "&:hover": { bgcolor: "#ffffff" },
                                            "&.Mui-focused": { bgcolor: "#ffffff", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.08)" }
                                        }
                                    }}
                                />
                            </Box>

                            {/* Google Meet Section */}
                            <Box sx={{
                                p: 3,
                                borderRadius: "24px",
                                background: meetingLink ? "linear-gradient(135deg, #f0f7ff 0%, #e1f5fe 100%)" : "linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%)",
                                border: meetingLink ? "1px solid rgba(26, 115, 232, 0.15)" : "1px dashed #dadce0",
                                display: "flex",
                                alignItems: "center",
                                gap: 2.5,
                                boxShadow: meetingLink ? "0 4px 15px rgba(26, 115, 232, 0.05)" : "none"
                            }}>
                                <Box sx={{
                                    width: 54,
                                    height: 54,
                                    borderRadius: "16px",
                                    bgcolor: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                                    flexShrink: 0
                                }}>
                                    <img src={GoogleMeetIcon} alt="Google Meet" style={{ width: 40, height: 40, objectFit: 'contain' }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    {meetingLink ? (
                                        <>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Live Class Room Added</Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>{meetingLink}</Typography>
                                            {!isReadOnly && (
                                                <Typography variant="caption" sx={{ color: "#1a73e8", cursor: "pointer", fontWeight: 500 }} onClick={() => {
                                                    setMeetingLink("");
                                                }}>Remove</Typography>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: formErrors.meetingLink ? "#d32f2f" : "inherit" }}>Video Conferencing <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                            <Typography variant="caption" color={formErrors.meetingLink ? "error" : "textSecondary"}>
                                                {formErrors.meetingLink ? formErrors.meetingLink : "Add a secure link for online classes"}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                {!meetingLink && !isReadOnly && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (!title.trim() || !startTime || !endTime || !localDate) {
                                                setSnackbar({
                                                    open: true,
                                                    message: "Please add a Class Title, Date, and Time to generate a link.",
                                                    severity: "error"
                                                });
                                                return;
                                            }
                                            handleGenerateLink();
                                        }}
                                        variant="contained"
                                        disabled={isMeetLoading}
                                        sx={{
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            bgcolor: "#1a73e8",
                                            px: 2,
                                            "&:hover": { bgcolor: "#1765cc" }
                                        }}
                                    >
                                        {isMeetLoading ? "Generating..." : "Add Link"}
                                    </Button>
                                )}
                                {meetingLink && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (meetingLink) {
                                                setIsJoinConfirmOpen(true);
                                            }
                                        }}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            px: 2
                                        }}
                                    >
                                        Test Join
                                    </Button>
                                )}
                            </Box>



                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                                <Button
                                    onClick={onClose}
                                    sx={{
                                        textTransform: "none",
                                        color: "#5f6368",
                                        fontWeight: 600,
                                        px: 4,
                                        borderRadius: "12px",
                                        fontSize: "14px"
                                    }}
                                >
                                    Cancel
                                </Button>
                                {!isReadOnly && (
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={isCreateLoading || !title.trim() || !localDate || !startTime || !endTime || !tutor || !subjectId || members.length === 0 || !meetingLink}
                                        sx={{
                                            textTransform: "none",
                                            background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
                                            borderRadius: "16px",
                                            color: "#ffffff",
                                            px: 6,
                                            py: 1.5,
                                            fontSize: "15px",
                                            fontWeight: 700,
                                            boxShadow: "0 8px 25px rgba(26, 115, 232, 0.3)",
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #1557b0 0%, #0a3d8a 100%)",
                                                boxShadow: "0 12px 30px rgba(26, 115, 232, 0.45)",
                                                transform: "translateY(-2px)"
                                            },
                                            "&:active": { transform: "translateY(0)" },
                                            "&.Mui-disabled": {
                                                opacity: 0.6,
                                                background: "rgba(0,0,0,0.12)",
                                                color: "rgba(0,0,0,0.26)",
                                                boxShadow: "none"
                                            }
                                        }}
                                    >
                                        {isCreateLoading ? "Scheduling..." : "Confirm Schedule"}
                                    </Button>
                                )}
                                {isReadOnly && (
                                    <Button
                                        variant="contained"
                                        onClick={onClose}
                                        sx={{
                                            textTransform: "none",
                                            bgcolor: "#5f6368",
                                            borderRadius: "12px",
                                            color: "#ffffff",
                                            px: 5,
                                            py: 1.2,
                                            fontSize: "14px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Close
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </motion.div>
                </DialogContent>
            </Dialog>

            {/* Booklet Preview Dialog with Security Features */}
            <Dialog
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                fullScreen
                PaperProps={{
                    sx: {
                        bgcolor: "rgba(0,0,0,0.9)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden"
                    }
                }}
            >
                <Box sx={{
                    height: 60,
                    bgcolor: "#202124",
                    display: "flex",
                    alignItems: "center",
                    px: 3,
                    justifyContent: "space-between",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    zIndex: 2000
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#9c27b0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MenuBookIcon sx={{ color: "#fff", fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ color: "#fff", fontWeight: 600 }}>{previewTitle}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2, px: 1.5, py: 0.5, bgcolor: "rgba(255,255,255,0.1)", borderRadius: "20px" }}>
                            <VisibilityOffIcon sx={{ color: "#fb7185", fontSize: 14 }} />
                            <Typography sx={{ color: "#fb7185", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>Protected Preview</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 3, color: "rgba(255,255,255,0.5)" }}>
                            <PrintDisabledIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">Printing & Downloads Disabled</Typography>
                        </Box>
                        <IconButton onClick={() => setIsPreviewOpen(false)} sx={{ color: "#fff" }}>
                            <X size={24} />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent
                    sx={{
                        p: 0,
                        flex: 1,
                        display: "flex",
                        position: "relative",
                        "&::-webkit-scrollbar": { display: "none" }
                    }}
                    onContextMenu={(e) => e.preventDefault()} // Block right-click
                >
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        pointerEvents: "none",
                        zIndex: 100,
                        background: "rgba(255,255,255,0.01)",
                        "@media print": { display: "none" }
                    }} />

                    <iframe
                        src={`${previewUrl.replace("/view", "/preview")}`}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        title="Booklet Preview"
                        onContextMenu={(e) => e.preventDefault()}
                    />
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%', borderRadius: "12px", boxShadow: "0 8px 16px rgba(0,0,0,0.1)" }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <GlobalStyles styles={{
                "@media print": {
                    "body, iframe, #root, .MuiDialog-root": {
                        display: "none !important",
                    }
                },
                // Fix for the black background issue in MUI TimePicker selected state
                ".MuiMultiSectionDigitalClock-root .Mui-selected": {
                    backgroundColor: "#1a73e8 !important",
                    color: "#fff !important"
                },
                ".MuiMultiSectionDigitalClockSection-item.Mui-selected": {
                    backgroundColor: "#1a73e8 !important",
                    color: "#fff !important"
                }
            }} />

            {/* Test Join Confirmation Dialog */}
            <Dialog
                open={isJoinConfirmOpen}
                onClose={() => setIsJoinConfirmOpen(false)}
                PaperProps={{
                    sx: { borderRadius: "20px", width: "100%", maxWidth: "400px", p: 1 }
                }}
            >
                <DialogContent>
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <Box sx={{
                            width: 60, height: 60, borderRadius: "50%", bgcolor: "#e8f0fe",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            mx: "auto", mb: 2
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                bgcolor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                            }}>
                                <img src={GoogleMeetIcon} alt="Google Meet" style={{ width: 43, height: 43, objectFit: 'contain' }} />
                            </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "14px", mb: 1, color: "#202124" }}>
                            Join Meeting?
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5f6368", fontSize: "14px", mb: 4, px: 2 }}>
                            This will open the Google Meet classroom in a new browser tab.
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    window.open(meetingLink, '_blank');
                                    setIsJoinConfirmOpen(false);
                                }}
                                sx={{
                                    fontSize: "13px",
                                    color: "#ffffff",
                                    textTransform: "none", bgcolor: "#009dff", borderRadius: "12px",
                                    py: 1.2, fontWeight: 600, boxShadow: "none",
                                    "&:hover": { bgcolor: "#1765cc", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.2)" }
                                }}
                            >
                                Open in New Tab
                            </Button>
                            <Button
                                fullWidth
                                onClick={() => setIsJoinConfirmOpen(false)}
                                sx={{
                                    fontSize: "13px",
                                    textTransform: "none", color: "#5f6368", fontWeight: 600,
                                    py: 1.2, "&:hover": { bgcolor: "#f1f3f4" }
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MeetingModal;
